import { HimmaContext } from '../Context/HimmaContext';
import { useState, useCallback, useContext } from 'react';
import {
  AddOrEditItemRequestSchema,
  DeleteSubAdminsRequest,
  ToggleSubAdminStatusRequest,
  BulkUploadItemAddedByEnum,
  BulkUploadResponseSchema,
  BulkUploadStatusResponseSchema,
} from '../client-sdk';
import { useStoreId } from '../components/stateless/ProductedRoute';

  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const localStorageStoreId = userData?.userDetails?.storeId; 

export const useStoreItemApi = () => {
  const api = useContext(HimmaContext)!;
  const contextStoreId = useStoreId(); // Get storeId from context
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const addStoreItem = useCallback(
    async (storeItemData: AddOrEditItemRequestSchema): Promise<any> => {
      setLoading(true);
      setError(null);

      try {
        const response = await api.addItem(storeItemData,localStorageStoreId);
        if (response?.data) {
          return response.data;
        }
        throw new Error('Failed to create item: No response data');
      } catch (err: unknown) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('An unexpected error occurred');
        }
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const getAllStoreItem = useCallback(
    async (query: any) => {
      setLoading(true);
      setError(null);
      try {
        // Prefer merchantId, and pass storeId explicitly when provided
        const merchantId: string | undefined = query.merchantId || query.storeId;
        const storeId: string | undefined = query.storeId;

        const response = await api.getAllItems(
          query.page,
          query.limit,
          query.sortBy,
          query.sortOrder,
          merchantId,
          storeId, // storeId filter
          undefined, // storeType - removed as requested
          query.search,
          query.stockAvailability,
          query.addedBy
        );
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const blockOrUnblockStoreItemById = useCallback(
    async (query: any) => {
      setLoading(true);
      setError(null);
      try {
        const toggleSubAdminStatusRequest: ToggleSubAdminStatusRequest = {
          ids: Array.isArray(query.id) ? query.id : [query.id],
          status: query.status,
        };
        const response = await api.toggleItemStatus(
          toggleSubAdminStatusRequest,localStorageStoreId
        );
        return response.data;
      
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const deleteStoreItemById = useCallback(
    async (query: string[]) => {
      setLoading(true);
      setError(null);
      try {
        const deleteCustomer: DeleteSubAdminsRequest = {
          ids: query,
        };
        const response = await api.deleteItems(deleteCustomer,localStorageStoreId);
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const editStoreItem = useCallback(
    async (itemId: any, storeItemData: AddOrEditItemRequestSchema) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.updateItem(itemId, storeItemData,localStorageStoreId );
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const bulkUploadItems = useCallback(
    async (
      file: File,
      addedBy: BulkUploadItemAddedByEnum,
      merchantId?: string
    ): Promise<BulkUploadResponseSchema> => {
      setLoading(true);
      setError(null);
      try {
        // Get storeId from multiple sources: context, localStorage storeId, or storeIds array
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');
        const storeIds = userData?.userDetails?.storeIds;
        const storeIdFromArray = Array.isArray(storeIds) && storeIds.length > 0 ? storeIds[0] : null;
        const storeId = contextStoreId || 
                       userData?.userDetails?.storeId || 
                       storeIdFromArray || 
                       localStorageStoreId;
        
        if (!storeId) {
          // Log for debugging
          console.error('StoreId not found. Available data:', {
            contextStoreId,
            storeIdFromUserDetails: userData?.userDetails?.storeId,
            storeIdsArray: storeIds,
            storeIdFromArray,
            localStorageStoreId,
            userDetails: userData?.userDetails
          });
          throw new Error('Store ID is required. Please ensure you are logged in and have selected a store.');
        }

        const response = await api.bulkUploadItem(
          storeId,
          addedBy,
          file,
          merchantId
        );
        if (response?.data) {
          return response.data;
        }
        throw new Error('Failed to upload items: No response data');
      } catch (err: any) {
        // Preserve the original error structure so mutation handler can access errors array
        // Don't create a new Error, just re-throw the original to preserve response.data
        setError(err?.response?.data?.error || err?.message || 'Failed to upload items');
        throw err; // Re-throw original error to preserve response.data structure
      } finally {
        setLoading(false);
      }
    },
    [api, contextStoreId]
  );

  const getBulkUploadStatus = useCallback(
    async (jobId: string): Promise<BulkUploadStatusResponseSchema> => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.getBulkUploadStatus(jobId);
        if (response?.data) {
          return response.data;
        }
        throw new Error('Failed to get bulk upload status: No response data');
      } catch (err: unknown) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('An unexpected error occurred');
        }
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const downloadBulkUploadTemplate = useCallback(
    async (): Promise<Blob> => {
      setLoading(true);
      setError(null);
      try {
        // Reuse the same storeId resolution logic as bulkUploadItems
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');
        const storeIds = userData?.userDetails?.storeIds;
        const storeIdFromArray =
          Array.isArray(storeIds) && storeIds.length > 0 ? storeIds[0] : null;
        const storeId =
          contextStoreId ||
          userData?.userDetails?.storeId ||
          storeIdFromArray ||
          localStorageStoreId;

        if (!storeId) {
          console.error('StoreId not found while downloading template. Available data:', {
            contextStoreId,
            storeIdFromUserDetails: userData?.userDetails?.storeId,
            storeIdsArray: storeIds,
            storeIdFromArray,
            localStorageStoreId,
            userDetails: userData?.userDetails,
          });
          throw new Error(
            'Store ID is required to download the template. Please ensure you are logged in and have selected a store.'
          );
        }

        const response = await api.downloadBulkUploadTemplate(storeId, {
          responseType: 'blob',
        });
        if (response?.data) {
          return response.data;
        }
        throw new Error('Failed to download template: No response data');
      } catch (err: unknown) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('An unexpected error occurred');
        }
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  return {
    loading,
    error,
    addStoreItem,
    editStoreItem,
    getAllStoreItem,
    blockOrUnblockStoreItemById,
    deleteStoreItemById,
    bulkUploadItems,
    getBulkUploadStatus,
    downloadBulkUploadTemplate,
  };
};
